"use client";

import { useContext, useEffect } from "react";

import { usePathname } from "next/navigation";

import {
  HSA_FSA_CART_PATH,
  HSA_FSA_ELIGIBLE_PDPS,
  HSA_FSA_EXPERIMENT_ID,
  HSA_FSA_EXPERIMENT_NAME,
  HSA_FSA_EXPERIMENT_VIEWED_COOKIE,
  HSA_FSA_TEST_COOKIE,
} from "@/constants/hsaFsa";
import CartContext from "@/context/CartContext";
import { isCartFullyHsaFsaEligible } from "@/utils/cart";
import { readCookie, setCookie } from "@/utils/cookies";
import { segmentEvent } from "@/utils/segmentfunctions";

const HSA_FSA_COHORTS = ["control", "variant"];
const EXPERIMENT_VIEWED_COOKIE_DAYS = 30;

/*
 * Cookies scoped to the shared root domain are readable by the separate Magento
 * checkout repo too — needed so the dedupe flag this sets can eventually be honored
 * there as well (see plan: Magento-side follow-up to check/set the same cookie).
 */
function getSharedCookieDomain(): string | undefined {
  if (typeof window === "undefined" || window.location.hostname === "localhost") return undefined;
  return ".nutrafol.com";
}

/**
 * Fires Segment's "Experiment Viewed" once per the dedupe window (30 days, shared across
 * PDP/Cart/checkout via a root-domain cookie) the first time a bucketed user reaches one of
 * the HSA/FSA test's touchpoint pages. On PDP this fires for either cohort just by landing
 * on one of the 5 core PDPs. On the cart page it only fires when every item in the cart is
 * HSA/FSA eligible — i.e. when the badge *would* show — regardless of the user's own cohort,
 * so control fires there too whenever their cart would have qualified under variant.
 */
export default function HsaFsaExperimentTracker(): null {
  const pathname = usePathname();
  const { cartData } = useContext(CartContext);

  useEffect(() => {
    const normalizedPath = pathname.replace(/\/$/, "");
    const isCorePdp = HSA_FSA_ELIGIBLE_PDPS.includes(normalizedPath);
    const isCartPage = normalizedPath === HSA_FSA_CART_PATH;
    if (!isCorePdp && !isCartPage) return;

    const cohort = readCookie(HSA_FSA_TEST_COOKIE);
    if (!cohort || !HSA_FSA_COHORTS.includes(cohort)) return;

    if (isCartPage) {
      /*
       * Fire for control too when their cart would have shown the badge had they been in
       * variant — eligibility depends only on cart contents, not on the user's own cohort.
       */
      const wouldShowCartBadge = isCartFullyHsaFsaEligible(cartData?.items);
      if (!wouldShowCartBadge) return;
    }

    if (readCookie(HSA_FSA_EXPERIMENT_VIEWED_COOKIE)) return;

    segmentEvent("Experiment Viewed", {
      experiment_id: HSA_FSA_EXPERIMENT_ID,
      experiment_name: HSA_FSA_EXPERIMENT_NAME,
      variant_name: cohort,
    });
    setCookie(
      HSA_FSA_EXPERIMENT_VIEWED_COOKIE,
      "true",
      EXPERIMENT_VIEWED_COOKIE_DAYS,
      "/",
      getSharedCookieDomain()
    );
  }, [pathname, cartData]);

  return null;
}
